import { CancelToken } from "apisauce";
import apiClient from "./client";

/**
 * Sends a File to the API in multipart pieces (POST documents/upload/chunk)
 * and resolves with the upload id the document endpoints accept in place of
 * `file_string`. Memory stays flat on both ends: the browser never base64s
 * the file and PHP streams each piece to disk.
 *
 * Retries a piece a few times on network/5xx errors; a 4xx aborts and the
 * pieces are discarded server-side. Pass an AbortSignal to cancel: the
 * in-flight piece is cut off (axios 0.21 cancel token) and the pieces are
 * discarded.
 */
const FALLBACK_CHUNK_BYTES = 8 * 1024 * 1024;
const RETRIES = 3;

let limitsPromise = null;

/** {max_chunk_bytes, max_total_bytes} from the API, fetched once. */
export const getUploadLimits = () => {
  if (!limitsPromise) {
    limitsPromise = apiClient.get("documents/upload/limits").then((response) => {
      if (response.ok && response.data && response.data.max_chunk_bytes) {
        return response.data;
      }
      limitsPromise = null;
      return { max_chunk_bytes: FALLBACK_CHUNK_BYTES, max_total_bytes: Infinity };
    });
  }
  return limitsPromise;
};

const uuid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // insecure contexts (plain http on a LAN host) have no randomUUID
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/** Drops the pieces of an unfinished upload server-side. Safe to call twice. */
export const discardUpload = (uploadId) =>
  apiClient.delete(`documents/upload/${uploadId}`).catch(() => {});

const sendChunk = async (uploadId, index, count, blob, signal) => {
  let response;
  for (let attempt = 0; attempt < RETRIES; attempt++) {
    if (signal && signal.aborted) return null;
    const form = new FormData();
    form.append("upload_id", uploadId);
    form.append("chunk_index", String(index));
    form.append("chunk_count", String(count));
    form.append("chunk", blob, "blob");
    const source = CancelToken.source();
    const onAbort = () => source.cancel("upload cancelled");
    if (signal) signal.addEventListener("abort", onAbort, { once: true });
    try {
      response = await apiClient.post("documents/upload/chunk", form, { cancelToken: source.token });
    } finally {
      if (signal) signal.removeEventListener("abort", onAbort);
    }
    if (signal && signal.aborted) return null;
    if (response.ok) return response;
    const status = response.status || 0;
    if (status >= 400 && status < 500 && status !== 429) return response;
  }
  return response;
};

/**
 * @param {File} file
 * @param {{onProgress?: (fraction: number) => void, signal?: AbortSignal}} [options]
 * @returns {Promise<{ok: true, uploadId: string} | {ok: false, aborted: boolean, status: number, response: any}>}
 */
export const uploadInChunks = async (file, { onProgress, signal } = {}) => {
  if (signal && signal.aborted) {
    return { ok: false, aborted: true, status: 0, response: null };
  }
  const limits = await getUploadLimits();
  if (file.size > limits.max_total_bytes) {
    return { ok: false, aborted: false, status: 413, response: null };
  }
  const chunkBytes = Math.min(FALLBACK_CHUNK_BYTES, limits.max_chunk_bytes);
  const count = Math.max(1, Math.ceil(file.size / chunkBytes));
  const uploadId = uuid();

  for (let index = 0; index < count; index++) {
    const blob = file.slice(index * chunkBytes, Math.min(file.size, (index + 1) * chunkBytes));
    const response = await sendChunk(uploadId, index, count, blob, signal);
    if (!response || !response.ok) {
      discardUpload(uploadId);
      const aborted = Boolean(signal && signal.aborted);
      return { ok: false, aborted, status: response ? response.status : 0, response };
    }
    if (onProgress) onProgress((index + 1) / count);
  }

  if (signal && signal.aborted) {
    discardUpload(uploadId);
    return { ok: false, aborted: true, status: 0, response: null };
  }

  return { ok: true, uploadId };
};

/**
 * Runs async tasks with at most `concurrency` in flight, so dropping ten
 * videos does not open ten parallel streams. Returns an `enqueue(task)`
 * that resolves with the task's result.
 */
export const createUploadQueue = (concurrency = 2) => {
  let active = 0;
  const waiting = [];
  const next = () => {
    if (active >= concurrency || waiting.length === 0) return;
    active++;
    const { task, resolve, reject } = waiting.shift();
    Promise.resolve()
      .then(task)
      .then(resolve, reject)
      .finally(() => {
        active--;
        next();
      });
  };
  return (task) =>
    new Promise((resolve, reject) => {
      waiting.push({ task, resolve, reject });
      next();
    });
};

export default uploadInChunks;
